import React from 'react';
import { CampaignPlan } from '@/services/campaignPlannerService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Target, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface CampaignPlanCardProps {
  plan: CampaignPlan;
  onEdit: () => void;
  onDelete: () => void;
}

const goalLabels: Record<string, string> = {
  seasonal: '🎉 Seasonal',
  lead_gen: '🎯 Lead Gen',
  retention: '🔄 Retention',
  product_launch: '🚀 Product Launch',
  custom: '✨ Custom',
};

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-primary/10 text-primary',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  archived: 'bg-muted text-muted-foreground',
};

export const CampaignPlanCard: React.FC<CampaignPlanCardProps> = ({ plan, onEdit, onDelete }) => {
  return (
    <Card className="group hover:shadow-md transition-shadow cursor-pointer" onClick={onEdit}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base line-clamp-1">{plan.name || 'Untitled Plan'}</CardTitle>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{goalLabels[plan.goal] || plan.goal}</Badge>
          <Badge className={`text-xs ${statusColors[plan.status] || ''}`}>{plan.status}</Badge>
        </div>
        {plan.idea_prompt && (
          <p className="text-xs text-muted-foreground line-clamp-2">{plan.idea_prompt}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {plan.scheduled_start && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(new Date(plan.scheduled_start), 'MMM d, yyyy')}
            </span>
          )}
          <span>{format(new Date(plan.created_at), 'MMM d, yyyy')}</span>
        </div>
      </CardContent>
    </Card>
  );
};
